import styled, { css } from "styled-components";
import { getToneTokens } from "../../styles/shared";
import { mq } from "../../styles/canonical/breakpoints";

// Kanban de máquinas. Card neutro: o estado aparece só no prazo (texto + ponto)
// e nos marcadores de cada ambiente. Cor comunica estado, nunca decora.

export const Legenda = styled.p`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  margin: 0 0 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};

  > span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const Colunas = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: start;

  /* Tablet: as 3 colunas ficam lado a lado com scroll horizontal INTERNO do
     board (único scroll-x permitido); a página não rola de lado. */
  @media ${mq.tablet} {
    grid-template-columns: repeat(3, minmax(260px, 1fr));
    gap: 10px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;
  }

  /* Celular: uma raia por vez (seletor de chips acima). */
  @media ${mq.mobile} {
    grid-template-columns: minmax(0, 1fr);
    overflow-x: visible;
  }
`;

/* Seletor de raia no celular (precedente: LaneTabs de Suprimentos). */
export const LaneTabs = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  white-space: nowrap;
  padding-bottom: 2px;
  margin-bottom: 10px;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const LaneChip = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${({ theme, $on }) => ($on ? theme.textPrimary : theme.border)};
  background: ${({ theme, $on }) => ($on ? theme.surfaceAlt : theme.surface)};
  color: ${({ theme, $on }) => ($on ? theme.textPrimary : theme.textSecondary)};
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $on }) => ($on ? 600 : 500)};
  cursor: pointer;

  .n {
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.textSecondary};
  }
  .atraso {
    color: ${({ theme }) => getToneTokens(theme, "danger").text};
    font-weight: 700;
  }
`;

export const Coluna = styled.section`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  padding: 10px;
  border-radius: 12px;
  background: ${({ theme }) => theme.surfaceAlt};
`;

export const ColunaTopo = styled.header`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 4px 4px;
`;

export const ColunaTitulo = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 650;
  color: ${({ theme }) => theme.textPrimary};
`;

export const ColunaContagem = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.textSecondary};
`;

export const ColunaAtraso = styled.strong`
  font-weight: 600;
  color: ${({ theme }) => getToneTokens(theme, "danger").text};
`;

export const ColunaVazia = styled.p`
  margin: 0;
  padding: 18px 12px;
  border: 1px dashed ${({ theme }) => theme.border};
  border-radius: 10px;
  font-size: 13px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
`;

export const Lista = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// Atrasado ganha a borda na cor do estado; os demais ficam com a borda neutra
// (a faixa do cabeçalho já diz o estado sem gritar).
export const Card = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
  overflow: hidden;
  border: 1px solid
    ${({ theme, $tone }) => ($tone === "danger" ? getToneTokens(theme, "danger").border : theme.border)};
  border-radius: 10px;
  background: ${({ theme }) => theme.surface};
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 140ms cubic-bezier(0.23, 1, 0.32, 1);

  &:hover {
    border-color: ${({ theme, $tone }) =>
      $tone === "danger" ? getToneTokens(theme, "danger").text : theme.textSecondary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.995);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:active {
      transform: none;
    }
  }
`;

// Faixa do cabeçalho no tom do estado: presença de cor sem pintar o card todo.
export const CardTopo = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 2px 12px;
  padding: 11px 14px 10px;
  background: ${({ theme, $tone }) =>
    $tone === "default" ? "transparent" : getToneTokens(theme, $tone).background};
`;

export const CardLote = styled.span`
  font-size: 15px;
  font-weight: 650;
  letter-spacing: -0.01em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.textPrimary};
`;

export const CardCliente = styled.span`
  grid-column: 1;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Prazo = styled.span`
  grid-column: 2;
  grid-row: 1 / span 2;
  align-self: start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  ${({ theme, $tone }) => {
    if ($tone === "default") {
      return css`
        border: 1px solid ${theme.border};
        color: ${theme.textSecondary};
      `;
    }
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${theme.surface};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const Ambientes = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px 0;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

// Nível 3 da hierarquia (lote > cliente > ambiente): menor, cinza e com
// espaçamento de letra, porque os nomes vêm em caixa alta da planilha.
export const Ambiente = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 4px 14px 4px 16px;
  font-size: 11.5px;

  @media ${mq.tablet} {
    font-size: var(--text-min, 12px);
  }
  font-weight: 500;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.textSecondary};

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Marcadores = styled.span`
  display: inline-flex;
  gap: 3px;
`;

export const Marcador = styled.span`
  display: inline-grid;
  place-items: center;
  width: 20px;
  height: 18px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;

  @media ${mq.tablet} {
    width: 22px;
    height: 20px;
    font-size: var(--text-min, 12px);
  }

  ${({ theme, $feito, $proxima }) => {
    if ($feito) {
      const tokens = getToneTokens(theme, "success");
      return css`
        background: ${tokens.background};
        border: 1px solid ${tokens.border};
        color: ${tokens.text};
      `;
    }
    // Próxima operação: contorno sólido em tinta forte. Pendências seguintes:
    // tracejado esmaecido (opacidade, não color-mix, por causa do html2canvas).
    if ($proxima) {
      return css`
        background: ${theme.surface};
        border: 1px solid ${theme.textPrimary};
        color: ${theme.textPrimary};
      `;
    }
    return css`
      background: transparent;
      border: 1px dashed ${theme.textSecondary};
      color: ${theme.textSecondary};
      opacity: 0.55;
    `;
  }}
`;

export const CardRodape = styled.div`
  padding: 7px 14px 9px;
  border-top: 1px solid ${({ theme }) => theme.border};
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
`;
